import { PageHeader } from "@/components/layout/page-header";
import { DeleteSetDialog } from "@/components/reconciliation/delete-set-dialog";
import { LastRunPanel } from "@/components/reconciliation/run-progress/LastRunPanel";
import { RerunConfirmDialog } from "@/components/reconciliation/run-progress/RerunConfirmDialog";
import { RunProgressBar } from "@/components/reconciliation/run-progress/RunProgressBar";
import { RunProgressTrigger } from "@/components/reconciliation/run-progress/RunProgressTrigger";
import { StatusBadge } from "@/components/reconciliation/status-badge";
import { useReconciliationToasts } from "@/components/reconciliation/use-reconciliation-toasts";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { TruncatedText } from "@/components/ui/truncation-tooltip";
import {
	deleteReconciliationSet,
	getReconciliationSet,
	getRunReport,
	getRunsList,
	prepareReconciliationSet,
} from "@/services/api";
import {
	formatMessage,
	reconciliationMessages,
	reconciliationResultMessages,
} from "@/services/messages";
import { useTabActions, useTabStore } from "@/stores/tab-store";
import type { FileRef, ReconciliationStatus, RunListItem } from "@efes/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

// Sprint 3 / A4: legacy `aggregate-by-work-kind` удалён.
const LEVEL_LABEL_KEY: Record<string, keyof typeof reconciliationMessages> = {
	position: "level_position",
	"grand-total": "level_grand_total",
	unified: "level_unified",
};

const MODE_LABEL_KEY: Record<string, keyof typeof reconciliationMessages> = {
	volume: "mode_volume",
	rate: "mode_rate",
	cost: "mode_cost",
};

function formatDate(value: string): string {
	const parsed = new Date(value);
	if (Number.isNaN(parsed.getTime())) return value;
	return parsed.toLocaleString("ru-RU", {
		day: "2-digit",
		month: "2-digit",
		year: "numeric",
		hour: "2-digit",
		minute: "2-digit",
	});
}

/**
 * Страница деталей набора сверки (T047, US1, US2/US3/US5 readonly view).
 *
 * Read-only отображение всех полей и файлов. Действия:
 * - «Запустить сверку» (отключена в этой фиче — появится в этапе 5).
 * - «Подготовить» / «Перепроверить готовность» — POST /sets/:id/prepare с toast'ами.
 * - «Редактировать» — открыть create-таб с setId.
 * - «Удалить» — AlertDialog → DELETE → закрыть таб.
 */
export function ReconciliationDetailPage() {
	const queryClient = useQueryClient();
	const {
		openReconciliationCreateTab,
		openReconciliationResultTab,
		closeTabsForSet,
		markRunResultStale,
	} = useTabActions();
	const toasts = useReconciliationToasts();

	// setId из активного detail-таба (id в формате `reconciliation-detail-{N}`).
	const setId = useTabStore((state) => {
		const active = state.tabs.find((t) => t.id === state.activeTabId);
		return active?.type === "reconciliation-detail" ? active.setId : undefined;
	});
	const [pendingDelete, setPendingDelete] = useState(false);
	const [issues, setIssues] = useState<string[]>([]);
	const [pendingRerun, setPendingRerun] = useState<((confirmed: boolean) => void) | null>(null);
	// Внутри-сессионный старт прогона: trigger отдаёт runId+taskId сразу после 202,
	// а refetch `recon-runs` приходит с задержкой (~250 мс). Без локального state'а
	// пришлось бы либо ждать refetch (бар появлялся бы с задержкой), либо рендерить
	// бар внутри trigger'а (раньше было — давало два бара одновременно).
	const [freshRun, setFreshRun] = useState<{ runId: string; taskId: string } | null>(null);

	const { data: set, isLoading } = useQuery({
		queryKey: ["reconciliation-set", setId],
		queryFn: () => (setId !== undefined ? getReconciliationSet(setId) : Promise.reject()),
		enabled: setId !== undefined,
	});

	// FR-007 (R3): Получаем список прогонов для restore-after-reload и блока «Последний прогон».
	// staleTime: 0 + refetchOnWindowFocus: true — обработает race-conditions автоматически.
	const { data: runs } = useQuery({
		queryKey: ["recon-runs", setId],
		queryFn: () => getRunsList(setId as number),
		enabled: setId !== undefined,
		staleTime: 0,
		refetchOnWindowFocus: true,
		retry: 3, // FR-028c: 3 ретрая с exp backoff.
	});

	const latestRun: RunListItem | undefined = runs?.[0];
	const isActiveRun = latestRun?.status === "pending" || latestRun?.status === "running";

	// Sprint 5 / B3 (T-B3.2): когда detail монтируется, проходим по открытым
	// табам `reconciliation-result-{prevRunId}` для того же setId — если есть
	// таб с runId, отличным от latestRun, помечаем его stale. Это закрывает
	// race: пользователь открыл result-таб, в фоне отработал re-run, открывает
	// detail — старые табы должны явно показать stale-overlay.
	const tabs = useTabStore((state) => state.tabs);
	useEffect(() => {
		if (setId === undefined || latestRun?.id === undefined) return;
		for (const tab of tabs) {
			if (tab.type !== "reconciliation-result") continue;
			if (tab.setId !== setId) continue;
			if (tab.runId === undefined) continue;
			if (tab.runId === latestRun.id) continue;
			if (tab.stale === true) continue;
			markRunResultStale(tab.runId);
		}
		// Реагируем только на смену latestRun.id и setId — tabs читаем для
		// итерации, но не подписываемся на ре-render каждый раз при apertura
		// нового таба (markRunResultStale идемпотентен через `if (target.stale)`
		// guard в store).
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [setId, latestRun?.id]);

	// `RunListItemSchema` не содержит `taskId`. Для restore прогресса при
	// активном прогоне делаем follow-up `getRunReport(runId)` — у `RunRecord`
	// есть `taskId`. Backend не модифицируется (FR-033).
	const { data: activeRunReport } = useQuery({
		queryKey: ["run-report-meta", latestRun?.id],
		queryFn: () => getRunReport(latestRun?.id ?? ""),
		enabled: isActiveRun && !!latestRun?.id,
		staleTime: 30 * 1000,
		refetchOnWindowFocus: false,
		retry: 3, // FR-028c
	});

	const restoredTaskId = isActiveRun && activeRunReport ? activeRunReport.run.taskId : null;
	// freshRun имеет приоритет — он установлен мгновенно после 202, а restoredTaskId
	// доедет 1-2 query refetch'а спустя. Сравниваем по runId, чтобы не «залипнуть»
	// на устаревшем freshRun, если за это время отработал ещё один прогон.
	const activeTaskId =
		freshRun && (!latestRun || latestRun.id === freshRun.runId) ? freshRun.taskId : restoredTaskId;
	const activeTabBar = isActiveRun || freshRun !== null;

	const prepareMutation = useMutation({
		mutationFn: (id: number) => prepareReconciliationSet(id),
		onSuccess: (result) => {
			if (set) {
				queryClient.invalidateQueries({ queryKey: ["reconciliation-set", set.id] });
				queryClient.invalidateQueries({ queryKey: ["reconciliation-sets"] });
				// Тосты вида "Готов к запуску" / "Перепроверено" убраны: prepare
				// теперь invisible шаг внутри клика "Запустить сверку". На успехе
				// сразу полетит runMutation со своим toast'ом, на провале — issues
				// отрисуются красным блоком ниже header'а (это и есть feedback).
				setIssues(result.ok ? [] : result.issues);
			}
		},
		onError: (err) => {
			toasts.showApiError(err);
		},
	});

	const deleteMutation = useMutation({
		mutationFn: (id: number) => deleteReconciliationSet(id),
		onSuccess: (_data, deletedSetId) => {
			toasts.showSuccess("deleted");
			// Каскад на бекенде уже удалил runs/discrepancies, но фронт держит
			// `["run-report", runId]` со staleTime: Infinity — без явной очистки
			// result-таб этого набора покажет «призрак» удалённых результатов.
			for (const run of runs ?? []) {
				queryClient.removeQueries({ queryKey: ["run-report", run.id] });
				queryClient.removeQueries({ queryKey: ["run-report-meta", run.id] });
			}
			queryClient.removeQueries({ queryKey: ["recon-runs", deletedSetId] });
			queryClient.removeQueries({ queryKey: ["reconciliation-set", deletedSetId] });
			queryClient.invalidateQueries({ queryKey: ["reconciliation-sets"] });
			closeTabsForSet(deletedSetId);
		},
		onError: (err) => {
			toasts.showApiError(err);
		},
	});

	if (isLoading || !set) {
		return (
			<div className="flex items-center justify-center py-16 text-sm text-slate-500">
				<Loader2 className="mr-2 h-4 w-4 animate-spin" /> {reconciliationMessages.loading}
			</div>
		);
	}

	const runConfig = getRunButtonConfig(set.status);

	/**
	 * Pre-run guard для слитой кнопки "Запустить сверку": прогоняет
	 * pre-flight (POST /prepare, read-only). Если ok — разрешает запуск.
	 * Если не ok — issues отрисовываются красным блоком ниже header'а через
	 * `setIssues`, запуск прерывается. На сетевой ошибке prepare также
	 * прерываемся, но к toast'у от mutation'а добавляем явное сообщение,
	 * чтобы пользователь понимал — это именно блокировка запуска.
	 */
	const handlePreflightGuard = async (): Promise<boolean> => {
		try {
			const result = await prepareMutation.mutateAsync(set.id);
			return result.ok;
		} catch {
			// Сетевая/серверная ошибка — `prepareMutation.onError` уже показал
			// generic-toast через showApiError. Контекст «не дали запустить»
			// добавляем отдельным toast'ом, чтобы пользователь не подумал,
			// что прогон стартовал «как-то».
			toast.error(reconciliationMessages.toast_preflight_check_failed);
			return false;
		}
	};

	const handleRunError = (errorMessage: string) => {
		toast.error(errorMessage || reconciliationResultMessages.run.failed_title);
	};

	const handleConfirmRequest = (): Promise<boolean> =>
		new Promise<boolean>((resolve) => {
			setPendingRerun(() => resolve);
		});

	return (
		<div className="mx-auto max-w-5xl space-y-6">
			<PageHeader
				title={formatMessage(reconciliationMessages.page_detail_title, { name: set.name })}
				meta={
					<>
						<StatusBadge status={set.status} />
						<span className="text-sm text-slate-500">
							{set.createdByName ?? reconciliationMessages.unknown_user} ·{" "}
							{formatDate(set.createdAt)}
						</span>
					</>
				}
				actions={
					<div className="flex flex-wrap items-center gap-2">
						{/* FR-001: кнопка всегда видна. Слитая логика prepare→run:
					    `runGuard` запускает pre-flight перед run; если pre-flight
					    падает — запуск прерывается, issues показываются под header'ом.
					    Кнопка disabled только во время активного прогона.
					    Прогресс-бар рендерится отдельным блоком ниже (FR-007). */}
						<RunProgressTrigger
							setId={set.id}
							setName={set.name}
							disabled={runConfig.disabled || activeTabBar}
							disabledTooltip={
								activeTabBar
									? reconciliationResultMessages.run.button_disabled_running
									: runConfig.tooltip
							}
							requireConfirmation={set.status === "completed"}
							onConfirmRequest={set.status === "completed" ? handleConfirmRequest : undefined}
							runGuard={handlePreflightGuard}
							extraPending={prepareMutation.isPending}
							onStarted={(runId, taskId) => setFreshRun({ runId, taskId })}
						/>
						<Button
							type="button"
							variant="outline"
							disabled={set.status === "running"}
							title={
								set.status === "running" ? reconciliationMessages.error_status_running : undefined
							}
							onClick={() => openReconciliationCreateTab(set.id)}
						>
							{reconciliationMessages.button_edit}
						</Button>
						<Button
							type="button"
							variant="outline"
							disabled={set.status === "running"}
							title={
								set.status === "running" ? reconciliationMessages.error_status_running : undefined
							}
							onClick={() => setPendingDelete(true)}
							className="border-red-200 bg-white text-red-700 hover:bg-red-50"
						>
							{reconciliationMessages.button_delete}
						</Button>
					</div>
				}
			/>

			{issues.length > 0 && (
				<div className="rounded-lg border border-red-200 bg-red-50 p-4">
					<h3 className="text-sm font-semibold text-red-800">
						{reconciliationMessages.toast_validation_error}
					</h3>
					<ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-red-700">
						{issues.map((issue) => (
							<li key={issue}>{issue}</li>
						))}
					</ul>
				</div>
			)}

			{/* FR-007: единственный рендер прогресс-бара. Покрывает оба сценария —
			    свежий старт в этой сессии (через `freshRun.taskId`) и restore после
			    перезагрузки страницы (через `restoredTaskId` из run-report-meta). */}
			{activeTabBar && activeTaskId && (
				<RunProgressBar
					taskId={activeTaskId}
					onComplete={(runId) => {
						setFreshRun(null);
						queryClient.invalidateQueries({ queryKey: ["recon-runs", set.id] });
						queryClient.invalidateQueries({ queryKey: ["reconciliation-set", set.id] });
						if (runId) {
							openReconciliationResultTab(set.id, runId, set.name);
						}
					}}
					onError={(message) => {
						setFreshRun(null);
						queryClient.invalidateQueries({ queryKey: ["recon-runs", set.id] });
						handleRunError(message);
					}}
				/>
			)}

			{/* FR-007/FR-031: блок «Последний прогон» для completed/failed. */}
			{!isActiveRun &&
				latestRun &&
				(latestRun.status === "completed" || latestRun.status === "failed") && (
					<LastRunPanel
						run={latestRun}
						onOpenResults={
							latestRun.status === "completed"
								? () => openReconciliationResultTab(set.id, latestRun.id, set.name)
								: undefined
						}
						onRetry={
							latestRun.status === "failed"
								? () => {
										// US3 расширит до полного retry-flow с диалогом /prepare+/run.
										// В US1 простой fallback: prepare + retry-флаг через mutation.
										prepareMutation.mutate(set.id);
									}
								: undefined
						}
					/>
				)}

			<div className="grid grid-cols-1 gap-4 md:grid-cols-2">
				<InfoCard label={reconciliationMessages.list_column_level}>
					{reconciliationMessages[LEVEL_LABEL_KEY[set.level] ?? "level_unified"]}
				</InfoCard>
				<InfoCard label={reconciliationMessages.list_column_modes}>
					<div className="flex flex-wrap gap-1">
						{set.modes.map((m) => (
							<Badge key={m} variant="pill" tone="neutral">
								{reconciliationMessages[MODE_LABEL_KEY[m]]}
							</Badge>
						))}
					</div>
				</InfoCard>
			</div>

			<FileSection
				label={reconciliationMessages.form_label_workorders}
				files={set.files.workOrders}
			/>
			<FileSection label={reconciliationMessages.form_label_acts} files={set.files.acts} />

			{pendingDelete && (
				<DeleteSetDialog
					set={set}
					onCancel={() => setPendingDelete(false)}
					onConfirm={() => deleteMutation.mutate(set.id)}
					isPending={deleteMutation.isPending}
				/>
			)}

			{pendingRerun && (
				<RerunConfirmDialog
					onConfirm={() => {
						pendingRerun(true);
						setPendingRerun(null);
					}}
					onCancel={() => {
						pendingRerun(false);
						setPendingRerun(null);
					}}
				/>
			)}
		</div>
	);
}

function getRunButtonConfig(status: ReconciliationStatus): {
	disabled: boolean;
	tooltip?: string;
} {
	const messages = reconciliationResultMessages.run;
	switch (status) {
		// draft/failed/completed — кнопка активна. Pre-flight выполняется
		// автоматически runGuard'ом перед стартом. Для completed дополнительно
		// показывается re-run confirm (FR-006).
		case "draft":
		case "failed":
		case "completed":
			return { disabled: false };
		case "running":
			return { disabled: true, tooltip: messages.button_disabled_running };
	}
}

function InfoCard({ label, children }: { label: string; children: React.ReactNode }) {
	return (
		<div className="rounded-lg border border-slate-200 bg-white p-4">
			<div className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</div>
			<div className="mt-1 text-sm text-slate-900">{children}</div>
		</div>
	);
}

function FileSection({ label, files }: { label: string; files: FileRef[] }) {
	return (
		<div className="space-y-2">
			<h2 className="text-sm font-semibold text-slate-700">
				{label} ({files.length})
			</h2>
			<div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
				<table className="min-w-full divide-y divide-slate-200">
					<thead className="bg-slate-50 text-xs font-medium uppercase tracking-wide text-slate-500">
						<tr>
							<th className="px-4 py-2 text-left">{reconciliationMessages.column_file}</th>
							<th className="px-4 py-2 text-left">{reconciliationMessages.column_sheet}</th>
							<th className="px-4 py-2 text-left">{reconciliationMessages.column_object}</th>
							<th className="px-4 py-2 text-left">{reconciliationMessages.column_period}</th>
							<th className="px-4 py-2 text-left">{reconciliationMessages.column_created}</th>
						</tr>
					</thead>
					<tbody className="divide-y divide-slate-200 text-sm">
						{files.map((f) => (
							<tr key={f.id}>
								<td className="px-4 py-2 text-slate-900">
									<TruncatedText value={f.originalName} className="max-w-[min(42rem,34vw)]" />
								</td>
								<td className="px-4 py-2 text-slate-700">
									{f.sheetName ? (
										<TruncatedText value={f.sheetName} className="max-w-[12rem]" />
									) : (
										"—"
									)}
								</td>
								<td className="px-4 py-2 text-slate-700">
									{f.objectName ? <TruncatedText value={f.objectName} className="max-w-xs" /> : "—"}
								</td>
								<td className="px-4 py-2 text-slate-700">
									{f.periodMonth || f.periodYear
										? `${f.periodMonth ? String(f.periodMonth).padStart(2, "0") : "??"}/${
												f.periodYear ?? "????"
											}`
										: "—"}
								</td>
								<td className="px-4 py-2 text-slate-500">{formatDate(f.createdAt)}</td>
							</tr>
						))}
					</tbody>
				</table>
			</div>
		</div>
	);
}
